import { useId, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getOverrideDateText } from '@/lib/shipment'

interface ShipmentOverrideDateFieldProps {
  label: string
  value: string | null
  defaultText: string
  min?: string
  isSaving: boolean
  onSave: (value: string | null) => Promise<boolean>
}

interface DateEditorProps {
  inputId: string
  value: string
  min?: string
  isSaving: boolean
  onChange: (value: string) => void
  onSave: () => void
  onCancel: () => void
}

interface DateDisplayProps {
  text: string
  hasOverride: boolean
  isSaving: boolean
  onEdit: () => void
  onReset: () => void
}

function DateEditor({ inputId, value, min, isSaving, onChange, onSave, onCancel }: DateEditorProps): React.ReactElement {
  const isBelowMin = min !== undefined && value < min
  const canSave = value !== '' && !isBelowMin && !isSaving

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input
        id={inputId}
        type="date"
        value={value}
        min={min}
        className="w-44 rounded-none"
        onChange={(event) => onChange(event.target.value)}
      />
      <Button
        type="button"
        className="rounded-none"
        disabled={!canSave}
        onClick={onSave}
      >
        Spara
      </Button>
      <Button
        type="button"
        variant="ghost"
        className="rounded-none"
        disabled={isSaving}
        onClick={onCancel}
      >
        Avbryt
      </Button>
    </div>
  )
}

function DateDisplay({ text, hasOverride, isSaving, onEdit, onReset }: DateDisplayProps): React.ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <p>{text}</p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="rounded-none"
        disabled={isSaving}
        onClick={onEdit}
      >
        Ändra
      </Button>
      {hasOverride && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="rounded-none"
          disabled={isSaving}
          onClick={onReset}
        >
          Återställ
        </Button>
      )}
    </div>
  )
}

export function ShipmentOverrideDateField({
  label,
  value,
  defaultText,
  min,
  isSaving,
  onSave
}: ShipmentOverrideDateFieldProps): React.ReactElement {
  const inputId = useId()
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState('')

  const hasOverride = value !== null

  const handleEdit = (): void => {
    setDraft(value ?? '')
    setIsEditing(true)
  }

  const handleSave = async (): Promise<void> => {
    const isSaved = await onSave(draft)

    if (isSaved) {
      setIsEditing(false)
    }
  }

  const handleReset = (): void => {
    void onSave(null)
  }

  const handleCancel = (): void => {
    setIsEditing(false)
  }

  return (
    <div className="space-y-2">
      <label
        htmlFor={inputId}
        className="text-sm text-muted-foreground"
      >
        {label}
      </label>

      {isEditing && (
        <DateEditor
          inputId={inputId}
          value={draft}
          min={min}
          isSaving={isSaving}
          onChange={setDraft}
          onSave={() => void handleSave()}
          onCancel={handleCancel}
        />
      )}

      {!isEditing && (
        <DateDisplay
          text={getOverrideDateText(value, defaultText)}
          hasOverride={hasOverride}
          isSaving={isSaving}
          onEdit={handleEdit}
          onReset={handleReset}
        />
      )}
    </div>
  )
}
